import { DEFAULT_CHILDREN_MAX_AGE, DEFAULT_INFANT_MAX_AGE } from "@majourneys/shared";

/**
 * Who a trip is for, and how that party meets each activity.
 *
 * A trip stores `people` (everyone, the number the planner has always kept)
 * and, since November 2026, `childAges`: one age per child in that number.
 * Anyone not in `childAges` is an adult. Keeping `people` as the head count
 * means every trip saved before children existed still reads the same, and
 * the account copy (`saved_trips.people`) keeps its meaning.
 *
 * A child's age is not enough on its own: each venue sets its own bands
 * (`infant_max_age`, `children_max_age`, with the site defaults behind them),
 * and some do not sell a child or infant ticket at all. `splitParty` applies
 * both, for one package, so the trip prices and books a family exactly as the
 * booking sheet would. Owner rule (2026-11-01): an activity a child cannot do
 * is still allowed in the plan; it is priced for the others and the day says
 * so, rather than being blocked.
 *
 * Pure and framework-free: the web planner and the mobile app both import it
 * (mobile through `@majourneys/discover/trips`).
 */

/** Oldest age the planner asks about; 17 is the last child birthday before adulthood anywhere we sell. */
export const MAX_CHILD_AGE = 17;
/** Most children one trip can carry, well under the party cap. */
export const MAX_TRIP_CHILDREN = 10;

export interface TripParty {
  readonly adults: number;
  readonly childAges: readonly number[];
}

/** One child age as the planner keeps it: a whole number, 0 to MAX_CHILD_AGE. */
export function cleanAge(age: unknown): number | null {
  if (typeof age !== "number" || !Number.isFinite(age)) return null;
  const whole = Math.floor(age);
  if (whole < 0 || whole > MAX_CHILD_AGE) return null;
  return whole;
}

/**
 * The party behind a trip. `people` is everyone; children come out of it, and
 * at least one adult always remains (a trip is never planned for children
 * alone). Bad or extra ages are dropped rather than trusted.
 */
export function partyOf(trip: { readonly people?: number; readonly childAges?: readonly number[] }, fallbackPeople = 2): TripParty {
  const people = typeof trip.people === "number" && trip.people >= 1 ? Math.round(trip.people) : fallbackPeople;
  const ages = (trip.childAges ?? []).map(cleanAge).filter((a): a is number => a !== null);
  const childAges = ages.slice(0, Math.max(0, Math.min(MAX_TRIP_CHILDREN, people - 1)));
  return { adults: Math.max(1, people - childAges.length), childAges };
}

/** Everyone in the party. */
export function partySize(party: TripParty): number {
  return party.adults + party.childAges.length;
}

/** What one package charges for, as far as who may come: -1 means "not sold". */
export interface PartyPricing {
  readonly child: number;
  readonly infant: number;
}

/** The venue's own age bands; null or absent falls back to the site default. */
export interface PartyBands {
  readonly infantMaxAge?: number | null;
  readonly childrenMaxAge?: number | null;
}

export interface PartySplit {
  /** Heads the booking sheet would book, by band. */
  readonly adults: number;
  readonly children: number;
  readonly infants: number;
  /** Children (of any band) who cannot come because the package sells them no ticket. */
  readonly leftOut: number;
  /**
   * The youngest age that can come, when some cannot: the venue's first adult
   * age if it sells no child ticket, its first child age if it sells no infant
   * ticket. For the "not for children under X" line. Null when nobody is left out.
   */
  readonly minAge: number | null;
}

function band(value: number | null | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
}

/**
 * Split a party for one package. Ages are placed in the venue's bands (the
 * same rule as `ageBracketLabels`, including its repair of a child band that
 * does not start after the infant band), then anyone whose band the package
 * does not sell is left out.
 */
export function splitParty(party: TripParty, bands: PartyBands, pricing: PartyPricing): PartySplit {
  const infantMax = band(bands.infantMaxAge, DEFAULT_INFANT_MAX_AGE);
  let childMax = band(bands.childrenMaxAge, DEFAULT_CHILDREN_MAX_AGE);
  if (childMax <= infantMax) childMax = infantMax + 1;

  let adults = Math.max(1, party.adults);
  let children = 0;
  let infants = 0;
  for (const age of party.childAges) {
    if (age <= infantMax) infants += 1;
    else if (age <= childMax) children += 1;
    else adults += 1; // a 16 or 17 year old at a venue whose adults start at 16
  }

  const noChild = pricing.child === -1;
  const noInfant = pricing.infant === -1;
  const leftOut = (noChild ? children : 0) + (noInfant ? infants : 0);
  let minAge: number | null = null;
  if (leftOut > 0) {
    if (noChild && children > 0) minAge = childMax + 1;
    else if (noInfant && infants > 0) minAge = infantMax + 1;
  }
  return {
    adults,
    children: noChild ? 0 : children,
    infants: noInfant ? 0 : infants,
    leftOut,
    minAge,
  };
}

/** Heads a split books. */
export function splitSize(split: PartySplit): number {
  return split.adults + split.children + split.infants;
}

/** ", " in the guest's language ("، " in Arabic). */
export function listSep(t: (key: string, opts?: Record<string, unknown>) => string): string {
  const sep = t("trip.list_sep");
  return sep === "trip.list_sep" ? ", " : sep;
}

/**
 * "2 adults, 2 children", or "2 people" when there are no children. Takes the
 * caller's `t`: the keys are the same on web (nested) and mobile (flat).
 */
export function partyLabel(t: (key: string, opts?: Record<string, unknown>) => string, party: TripParty): string {
  if (party.childAges.length === 0) return t("trip.when.people", { count: party.adults });
  return [
    t("trip.when.adults_count", { count: party.adults }),
    t("trip.when.children_count", { count: party.childAges.length }),
    // "، " in Arabic: a Latin comma read as a typo there (2026-11-04).
  ].join(listSep(t));
}
